import React, {useCallback, useEffect, useMemo, useState} from "react";
import {FixTransactionInvoicesDataGridColumnNames, FixTransactionMissingTransactionDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, DataGridColumnWidthTypes} from "../../../../packages/data-grid";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Row} from "reactstrap";
import PartnerDataGrid from "../../../containers/data-grid";
import Button from "../../base/button/button";
import {DefaultPaginationInfo} from "../../../../core/constants/enums";
import useIsMounted from "../../../hooks/use-is-mounted";
import FixTransactionDialogFinancialSummary from "./financial-summary";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import DateUtils from "../../../../core/services/date-utils";
import {TransactionTroubleShootingStatuses} from "../../../views/panel/operator/damage-control/transactions";

const initialOrderBy = null;
const initialPaginationInfo = {
    ...DefaultPaginationInfo,
}


const FixTransactionDialog = ({open, close, transaction, updateTransaction}) => {
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [creatingTransaction, setCreatingTransaction] = useState(false);
    const [missingTransaction, setMissingTransaction] = useState(null);
    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(initialOrderBy);
    const [connecting, setConnecting] = useState([]);
    const isMounted = useIsMounted();

    /**
     * With each change in the [open] prop:
     * - reset the state of the component
     */
    useEffect(() => {
        if (!open) {
            setInvoices([]);
            setCreatingTransaction(false);
            setConnecting([]);
            setPaginationInfo(initialPaginationInfo);
            setOrderBy(initialOrderBy);
            setLoading(false);
            return;
        }
        if (transaction?.statusId === TransactionTroubleShootingStatuses.outstandingBalance) {
            searchInvoices().then();
        } else {
            getPaymentMissingTransaction().then();
        }
    }, [open, transaction?.statusId])

    /**
     * Searches the invoices for a customer.
     * @type {(function(): Promise<void>)}
     */
    const searchInvoices = useCallback(async () => {
        const forApi = {
            customerId: transaction.customerId,
            paginationInfo: paginationInfo,
            transactionId: transaction.id,
        }
        setLoading(true);
        // TODO: call api to get invoices of a customer for transaction fix.
        const response = {
            resultFlag: true,
            data: {
                total: 20,
                items: Array.from({length: 10}).map((e, index) => ({
                    invoiceNo: `INV-${index + 1}`,
                    date: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                    amount: 1000,
                    balance: 1000,
                    applied: 0,
                    remaining: 1000,
                    connected: false,
                })),
            },
        }
        if (!isMounted())
            return;
        setLoading(false);
        if (response.resultFlag) {
            setInvoices(response.data.items);
            setPaginationInfo(prevState => ({...prevState, length: response.data.total}));
        }
    }, [isMounted, paginationInfo, transaction?.customerId, transaction?.id])

    /**
     * Fetches the missing transaction for a payment.
     * @type {(function(): Promise<void>)}
     */
    const getPaymentMissingTransaction = useCallback(async () => {
        setLoading(true);
        // TODO: call api to get invoices of a customer for transaction fix.
        const response = {
            resultFlag: true,
            data: {
                transactionNo: 'TRN-1',
                date: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                reference: 'REF-1',
                amount: 1000,
            }
        }
        if (!isMounted())
            return;
        setLoading(false);
        if (response.resultFlag) {
            setMissingTransaction(response.data);
        }
    }, [isMounted]);

    /**
     * Connects an invoice to a transaction in the server.
     * @type {(function(*): Promise<void>)}
     */
    const connectInvoiceToTransaction = useCallback(async (invoice) => {
        setConnecting(prevState => [...prevState, invoice.invoiceNo]);
        // TODO: call api for connecting an invoice to a transaction.
        const response = {
            resultFlag: true,
            data: {
                invoiceNo: invoice.invoiceNo,
                invoiceBalance: 1000,
                invoiceApplied: 100,
                invoiceRemaining: 100,
                transactionBalance: 1000,
            }
        }
        if (!isMounted())
            return;
        setConnecting(prevState => prevState.filter(e => e !== invoice.invoiceNo));
        if (response.resultFlag) {
            setInvoices(prevState => prevState.map(e => e.invoiceNo === invoice.invoiceNo
                ? {
                    ...e,
                    balance: response.data.invoiceBalance,
                    applied: response.data.invoiceApplied,
                    remaining: response.data.invoiceRemaining,
                    connected: true,
                }
                : e
            ));
            const okayNow = response.data.transactionBalance === 0;
            updateTransaction(transaction.id, prevState => ({
                ...prevState,
                balance: response.data.transactionBalance,
                statusId: okayNow ? TransactionTroubleShootingStatuses.okay : prevState.statusId,
            }));
        }
    }, [isMounted, transaction?.id, updateTransaction])

    /**
     * Disconnects an invoice from a transaction in the server.
     * @type {(function(*): Promise<void>)}
     */
    const disconnectInvoiceFromTransaction = useCallback(async (invoice) => {
        setConnecting(prevState => [...prevState, invoice.invoiceNo]);
        // TODO: call api for disconnecting an invoice from a transaction.
        const response = {
            resultFlag: true,
            data: {
                invoiceNo: invoice.invoiceNo,
                invoiceBalance: 1000,
                invoiceApplied: 100,
                invoiceRemaining: 100,
                transactionBalance: 1000,
            }
        }
        if (!isMounted())
            return;
        setConnecting(prevState => prevState.filter(e => e !== invoice.invoiceNo));
        if (response.resultFlag) {
            setInvoices(prevState => prevState.map(e => e.invoiceNo === invoice.invoiceNo
                ? {
                    ...e,
                    balance: response.data.invoiceBalance,
                    applied: response.data.invoiceApplied,
                    remaining: response.data.invoiceRemaining,
                    connected: false,
                }
                : e
            ));
            updateTransaction(transaction.id, prevState => ({
                ...prevState,
                balance: response.data.transactionBalance,
            }));
        }
    }, [isMounted, transaction?.id, updateTransaction])

    /**
     * Creates a missing transaction in the server.
     * @type {(function())}
     */
    const createMissingTransaction = useCallback(() => {
        setCreatingTransaction(true);
        const response = {
            resultFlag: true,
        }
        if (!isMounted())
            return;
        setCreatingTransaction(false);
        if (response.resultFlag) {
            updateTransaction(transaction.id, prevState => ({
                ...prevState,
                statusId: TransactionTroubleShootingStatuses.outstandingBalance,
            }));
        }
    }, [isMounted, updateTransaction, transaction?.id])

    /**
     * Sorts the entries of the credits in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = (tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: 1}));
    }

    const columns = useMemo(() =>
        /** @type {Array<DataGridColumn>}*/
        [
            {
                name: FixTransactionInvoicesDataGridColumnNames.invoiceNo,
                title: "Invoice No",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
            },
            {
                name: FixTransactionInvoicesDataGridColumnNames.date,
                title: "Invoice Date",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
            },
            {
                name: FixTransactionInvoicesDataGridColumnNames.amount,
                title: "Amount",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
            },
            {
                name: FixTransactionInvoicesDataGridColumnNames.balance,
                title: "Balance",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
            },
            {
                name: FixTransactionInvoicesDataGridColumnNames.applied,
                title: "Applied",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
            },
            {
                name: FixTransactionInvoicesDataGridColumnNames.remaining,
                title: "Remaining",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
            },
            {
                name: FixTransactionInvoicesDataGridColumnNames.action,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinnedType: DataGridColumnPinnedTypes.right,
                pinned: true,
                resizable: false
            },
        ], [])


    /**@type {Array<DataGridRow>}*/
    const rows = useMemo(() => {
        return invoices?.map((e, index) => ({
            key: index,
            data: e.data,
            cells: {
                [FixTransactionInvoicesDataGridColumnNames.invoiceNo]: e.invoiceNo,
                [FixTransactionInvoicesDataGridColumnNames.date]: e.date,
                [FixTransactionInvoicesDataGridColumnNames.amount]: e.amount,
                [FixTransactionInvoicesDataGridColumnNames.balance]: e.balance,
                [FixTransactionInvoicesDataGridColumnNames.applied]: e.applied,
                [FixTransactionInvoicesDataGridColumnNames.remaining]: e.remaining,
                [FixTransactionInvoicesDataGridColumnNames.action]:
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={connecting.includes(e.invoiceNo)}
                        onClick={() => e.connected ? disconnectInvoiceFromTransaction(e) : connectInvoiceToTransaction(e)}
                    >
                        {
                            connecting.includes(e.invoiceNo)
                                ? e.connected ? "Disconnecting..." : "Connecting"
                                : e.connected ? "Disconnect" : "Connect"
                        }
                    </Button>,
            }
        })) ?? []
    }, [connectInvoiceToTransaction, connecting, disconnectInvoiceFromTransaction, invoices])


    const missingTransactionsColumns = useMemo(() =>
        /** @type {Array<DataGridColumn>}*/
        [
            {
                name: FixTransactionMissingTransactionDataGridColumnNames.transactionNo,
                title: "Transaction No",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
                resizable: false,
                sortable: false,
                reorderable: false,
            },
            {
                name: FixTransactionMissingTransactionDataGridColumnNames.date,
                title: "Date",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
                resizable: false,
                sortable: false,
                reorderable: false,
            },
            {
                name: FixTransactionMissingTransactionDataGridColumnNames.reference,
                title: "Reference",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
                resizable: false,
                sortable: false,
                reorderable: false,
            },
            {
                name: FixTransactionMissingTransactionDataGridColumnNames.amount,
                title: "Amount",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
                resizable: false,
                sortable: false,
                reorderable: false,
            }
        ], [])

    /**@type {Array<DataGridRow>}*/
    const missingTransactionsRows = useMemo(() => {
        return [{
            key: 0,
            data: missingTransaction,
            cells: {
                [FixTransactionMissingTransactionDataGridColumnNames.transactionNo]: missingTransaction?.transactionNo,
                [FixTransactionMissingTransactionDataGridColumnNames.date]: missingTransaction?.date,
                [FixTransactionMissingTransactionDataGridColumnNames.reference]: missingTransaction?.reference,
                [FixTransactionMissingTransactionDataGridColumnNames.amount]: missingTransaction?.amount,
            }
        }];
    }, [missingTransaction]);

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={transaction?.statusId !== TransactionTroubleShootingStatuses.outstandingBalance ? 'md' : 'lg'}
                fullWidth
            >
                <DialogTitle>
                    {
                        transaction?.statusId === TransactionTroubleShootingStatuses.outstandingBalance
                            ? "Fix Outstanding Balance"
                            : transaction?.statusId === TransactionTroubleShootingStatuses.missingTransaction
                                ? "Fix Missing Transaction"
                                : "Fix Transaction"
                    }
                </DialogTitle>
                <DialogContent>
                    {
                        transaction?.statusId === TransactionTroubleShootingStatuses.missingTransaction &&
                        <>
                            <p className={'text-sm'}>
                                We need to create an AR transaction on our system to fix this issue. We have found the
                                following record on
                                your accounting system that we can use to create the transaction. Please confirm the
                                details below are
                                correct.
                            </p>
                            {
                                loading &&
                                <LoadingLottieAnimation width={'50%'}/>
                            }
                            {
                                !loading &&
                                <PartnerDataGrid
                                    rows={missingTransactionsRows}
                                    columns={missingTransactionsColumns}
                                    hideFooter
                                    hideToolbar
                                />
                            }
                        </>
                    }
                    {
                        transaction?.statusId === TransactionTroubleShootingStatuses.outstandingBalance &&
                        <>
                            <p className={'text-sm'}>
                                Connect this payment to the following invoices to fix the outstanding balance.
                                The following invoices are for <span className={'font-weight-semi-bold'}>
                                {transaction?.customerTitle ?? ''}
                            </span>.
                            </p>
                            <div className={'mt-3'}>
                                <PartnerDataGrid
                                    rows={rows}
                                    columns={columns}
                                    hideFooter
                                    hideExporting
                                    visibleRows={5}
                                    state={{
                                        pagination: paginationInfo,
                                        sortBy: orderBy,
                                        loading: {state: loading},
                                    }}
                                    onSortByChanged={(_, current) => onSort(current)}
                                    onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                                    onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                                />
                            </div>
                            <FixTransactionDialogFinancialSummary
                                amount={transaction?.amount}
                                balance={transaction?.balance}
                            />
                        </>
                    }
                </DialogContent>
                <DialogActions className={'p-0 m-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                type={'button'}
                                borderRadius={'0 8px 0 8px'}
                                onClick={close}
                            >
                                Cancel
                            </Button>
                            {
                                transaction?.statusId === TransactionTroubleShootingStatuses.missingTransaction &&
                                <Button
                                    disabled={!open || creatingTransaction}
                                    variant={'dialog'}
                                    color={'primary'}
                                    borderRadius={'8px 0 8px 0'}
                                    onClick={createMissingTransaction}
                                >
                                    {
                                        creatingTransaction
                                            ? "Creating..."
                                            : "Create Transaction"
                                    }

                                </Button>
                            }
                            {
                                transaction?.statusId === TransactionTroubleShootingStatuses.outstandingBalance &&
                                <Button
                                    disabled={!open}
                                    variant={'dialog'}
                                    color={'primary'}
                                    borderRadius={'8px 0 8px 0'}
                                    onClick={close}
                                >
                                    Done
                                </Button>
                            }
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FixTransactionDialog;
